import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { LayoutDashboard, Beaker, LogOut, Sparkles, Menu, Factory, Package, Settings, Crown, MessageSquare, Shield, Users, Cloud } from 'lucide-react';
import AIAssistant from '@/components/AIAssistant';

const MainLayout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard DIP', testId: 'nav-dashboard' },
    { path: '/products', icon: Beaker, label: 'Produits DIP', testId: 'nav-products' },
    { path: '/production', icon: Factory, label: 'Production', testId: 'nav-production', divider: true },
    { path: '/lab-profile', icon: Settings, label: 'Profil Labo', testId: 'nav-lab-profile', divider: true },
    { path: '/subscription', icon: Crown, label: 'Abonnement', testId: 'nav-subscription' },
    { path: '/support', icon: MessageSquare, label: 'Support', testId: 'nav-support' },
    ...(user?.is_admin ? [
      { path: '/admin/users', icon: Users, label: 'Admin Comptes', testId: 'nav-admin-users', divider: true },
      { path: '/admin/support', icon: Shield, label: 'Admin SAV', testId: 'nav-admin-support' },
      { path: '/admin/backup', icon: Cloud, label: 'Sauvegardes', testId: 'nav-admin-backup' },
    ] : []),
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 border-r border-border bg-card">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DIP PILOTE +IA" className="h-10 w-10 object-contain" />
            <div>
              <h1 className="text-lg font-semibold text-primary tracking-tight">DIP PILOTE +IA</h1>
              <p className="text-xs text-muted-foreground">Cosmétiques Artisans</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <div key={item.path}>
              {item.divider && <div className="h-px bg-border my-3"></div>}
              <button
                data-testid={item.testId}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                <item.icon size={20} strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </button>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <Button
            data-testid="btn-ai-assistant"
            onClick={() => setAiOpen(true)}
            variant="outline"
            className="w-full justify-start gap-3"
          >
            <Sparkles size={20} strokeWidth={1.5} />
            <span>Assistant IA</span>
          </Button>

          <div className="px-4 py-3 bg-muted rounded-md">
            <p className="text-sm font-medium text-foreground">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>

          <Button
            data-testid="btn-logout"
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive"
          >
            <LogOut size={20} strokeWidth={1.5} />
            <span>Déconnexion</span>
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="DIP PILOTE +IA" className="h-8 w-8 object-contain" />
            <h1 className="text-xl font-semibold text-primary">DIP PILOTE +IA</h1>
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 space-y-2">
                {menuItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-md ${
                      isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
              <div className="mt-6 space-y-3">
                <Button onClick={() => setAiOpen(true)} variant="outline" className="w-full justify-start gap-3">
                  <Sparkles size={20} />
                  Assistant IA
                </Button>
                <Button onClick={onLogout} variant="ghost" className="w-full justify-start gap-3 text-destructive">
                  <LogOut size={20} />
                  Déconnexion
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 lg:pt-0">
        <Outlet />
      </main>

      {/* AI Assistant Sheet */}
      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent className="w-full sm:max-w-2xl flex flex-col overflow-hidden">
          <SheetHeader className="shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="text-accent" size={24} />
              Assistant IA
            </SheetTitle>
            <SheetDescription>
              Production & Réglementation Cosmétique
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <AIAssistant user={user} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MainLayout;
